import { EVENT_STATUS } from "./eventConstants";
import { resolveEventStatus } from "./eventStatusUtils";

export const normalizeOptionalApiString = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  const normalized = String(value).trim();
  return normalized.toUpperCase() === "NA" ? "" : normalized;
};

export const normalizeEventApiItem = (data) => {
  if (!data || typeof data !== "object") {
    return data;
  }

  const eventLocation = normalizeOptionalApiString(
    data.eventLocation ?? data.EventLocation
  );
  const metaTitle = normalizeOptionalApiString(
    data.metaTitle ?? data.MetaTitle
  );
  const metaDescription = normalizeOptionalApiString(
    data.metaDescription ?? data.MetaDescription
  );
  const metaKeywords = normalizeOptionalApiString(
    data.metaKeywords ?? data.MetaKeywords
  );

  return {
    ...data,
    eventLocation,
    EventLocation: eventLocation,
    metaTitle,
    MetaTitle: metaTitle,
    metaDescription,
    MetaDescription: metaDescription,
    metaKeywords,
    MetaKeywords: metaKeywords,
  };
};

const appendPayloadValue = (payload, key, value) => {
  if (value instanceof File) {
    payload.append(key, value);
    return;
  }
  if (value === undefined || value === null) {
    payload.append(key, "");
    return;
  }
  payload.append(key, String(value));
};

const OPTIONAL_TEXT_SENTINEL = "NA";

const appendOptionalTextField = (payload, camelKey, pascalKey, value) => {
  const normalized = normalizeOptionalApiString(value);
  const safeValue = normalized || OPTIONAL_TEXT_SENTINEL;
  appendPayloadValue(payload, camelKey, safeValue);
  appendPayloadValue(payload, pascalKey, safeValue);
};

const appendDateField = (payload, camelKey, pascalKey, value) => {
  const normalized = normalizeOptionalApiString(value);
  appendPayloadValue(payload, camelKey, normalized);
  appendPayloadValue(payload, pascalKey, normalized);
};

/**
 * Builds multipart payload for the External Events API.
 */
export const buildEventFormData = (formData, isEditMode, id, status) => {
  const payload = new FormData();

  if (isEditMode && id) {
    payload.append("id", id);
    payload.append("Id", id);
  }

  payload.append("eventName", String(formData.eventName ?? "").trim());
  payload.append("EventName", String(formData.eventName ?? "").trim());
  payload.append("eventSlug", String(formData.eventSlug ?? "").trim());
  payload.append("EventSlug", String(formData.eventSlug ?? "").trim());

  appendOptionalTextField(
    payload,
    "shortDesc",
    "ShortDesc",
    formData.shortDescription
  );
  payload.append("fullDesc", formData.fullDescription || "");
  payload.append("FullDesc", formData.fullDescription || "");

  appendOptionalTextField(
    payload,
    "eventLocation",
    "EventLocation",
    formData.eventLocation
  );

  appendDateField(payload, "eventsFrom", "EventsFrom", formData.eventsFrom);
  appendDateField(payload, "eventsTo", "EventsTo", formData.eventsTo);

  if (formData.thumbImageUrl) {
    payload.append("thumbImage", String(formData.thumbImageUrl));
    payload.append("ThumbImage", String(formData.thumbImageUrl));
  }

  payload.append("isFeatured", String(Boolean(formData.isFeatured)));
  payload.append("featuredHome", String(Boolean(formData.isFeaturedHome)));
  const resolvedDisplayOrder =
    String(formData.displayOrder ?? "").trim() || "10000";
  appendPayloadValue(payload, "displayOrder", resolvedDisplayOrder);

  appendOptionalTextField(payload, "metaTitle", "MetaTitle", formData.metaTitle);
  appendOptionalTextField(
    payload,
    "metaDescription",
    "MetaDescription",
    formData.metaDescription
  );
  appendOptionalTextField(
    payload,
    "metaKeywords",
    "MetaKeywords",
    formData.metaKeywords
  );

  // API contract requires exactly these two keys — no PascalCase/snake_case
  // duplicates like the other fields in this file use.
  payload.append("ctaText", String(formData.ctaText ?? "").trim());
  payload.append("ctaLink", String(formData.ctaLink ?? "").trim());

  const resolvedStatus = status || EVENT_STATUS.DRAFT;
  payload.append("status", resolvedStatus);
  payload.append("Status", resolvedStatus);

  return payload;
};

export const mapEventResponse = (data) => {
  const normalized = normalizeEventApiItem(data);

  return {
    eventName: normalized.eventName || normalized.EventName || "",
    eventSlug: normalized.eventSlug || normalized.EventSlug || "",
    shortDescription:
      normalized.shortDesc ||
      normalized.ShortDesc ||
      normalized.shortDescription ||
      normalized.ShortDescription ||
      "",
    fullDescription:
      normalized.fullDesc ||
      normalized.FullDesc ||
      normalized.fullDescription ||
      normalized.FullDescription ||
      "",
    eventLocation: normalized.eventLocation,
    eventsFrom: normalized.eventsFrom || normalized.EventsFrom || "",
    eventsTo: normalized.eventsTo || normalized.EventsTo || "",
    isFeatured: Boolean(normalized.isFeatured ?? normalized.IsFeatured ?? false),
    isFeaturedHome: Boolean(
      normalized.featuredHome ??
        normalized.FeaturedHome ??
        normalized.isFeaturedHome ??
        normalized.IsFeaturedHome ??
        false
    ),
    displayOrder:
      normalized.displayOrder !== undefined && normalized.displayOrder !== null
        ? String(normalized.displayOrder)
        : normalized.DisplayOrder !== undefined &&
            normalized.DisplayOrder !== null
          ? String(normalized.DisplayOrder)
          : "",
    metaTitle: normalized.metaTitle,
    metaDescription: normalized.metaDescription,
    metaKeywords: normalized.metaKeywords,
    status: resolveEventStatus(normalized),
    thumbImageUrl:
      normalized.thumbImage ||
      normalized.ThumbImage ||
      normalized.thumbImageUrl ||
      normalized.ThumbImageUrl ||
      "",
    thumbImagePreview:
      normalized.thumbImage ||
      normalized.ThumbImage ||
      normalized.thumbImageUrl ||
      normalized.ThumbImageUrl ||
      "",
    thumbImageFile: null,
    ctaText: normalized.ctaText || normalized.CtaText || "",
    ctaLink: normalized.ctaLink || normalized.CtaLink || "",
  };
};
